import { App } from 'BMapsSrc/BMapsApp';
import { Compound, CompoundHeritage } from 'BMapsModel';
import { ResponseIds } from '../server/server_common';
import { UserActions, UserCmd } from './UserCmd';

import { integrateCompounds } from './cmds_common';
import { getFullResidueId, joinAtomSpecs } from '../util/mol_info_utils';
import { showAlert } from '../utils';
import { matrixToOrientation, Sub3_3, TransposeRTTransform } from '../math';
import {
    DataServiceFragmentSearchResult, BfdServerFragmentSearchResult, FragmentSearchResult,
} from '../model/FragmentSearchResult';
import { bondableSearchResults, removeClashingFragmentResults } from './fragment_geometry';

/**
 * @typedef {import('BMapsModel').Atom} Atom
 * @typedef {import('BMapsModel').CaseData} CaseData
 * @typedef {import('BMapsModel').DecodedFragment} DecodedFragment
 */

/**
 * @typedef ModificationCmds
 * @type {object}
 * @property {replaceGroup} ReplaceGroup
 * @property {growFromAtom} GrowFromAtom
 * @property {searchNear} SearchNear
 * @property {growToFragmentName}GrowToFragmentName
 * @property {fragDataQuery} FragDataQuery
 * @property {replaceWithFragmentSet} ReplaceWithFragmentSet
 */

/** @type {ModificationCmds} */
export const ModificationCmds = {
    ReplaceGroup: new UserCmd('ReplaceGroup', replaceGroup),
    GrowFromAtom: new UserCmd('GrowFromAtom', growFromAtom),
    SearchNear: new UserCmd('SearchNear', searchNear),
    ConfirmModification: new UserCmd('ConfirmModification', confirmModification),
    GrowToFragmentName: new UserCmd('GrowToFragmentName', growToFragmentName),
    FragDataQuery: new UserCmd('FragDataQuery', fragDataQuery),
    ReplaceWithFragmentSet: new UserCmd('ReplaceWithFragmentSet', replaceWithFragmentSet),
};

/**
 * Replace the bond between two atoms using fragments from a named fragment set.
 * Note: Not yet implemented on the server side.
 * @param {Atom} fromAtom - Source atom defining the bond vector start
 * @param {Atom} toAtom - Terminal atom defining the bond vector end
 * @param {string} fragsetName - Name of the fragment set to use for replacement
 * @returns {Promise<{ compounds: Compound[], errors: string[] }>}
 */
async function replaceWithFragmentSet(fromAtom, toAtom, fragsetName) {
    const { atomGroup: compound, connector, caseData } = App.getDataParents(fromAtom);

    const responseData = await connector.cmdReplaceWithFragset(
        {
            from: fromAtom.uniqueID,
            to: toAtom.uniqueID,
            fragset: fragsetName,
        }
    );

    showAlert('Replace with Fragment Set', 'This awesome dev-only feature is Not Yet Implemented!');

    // The below may or may not be a useful skeleton for handling returned data.
    // const newCompounds = responseData.byId(ResponseIds.Compound);
    // App.Workspace.addReplaceCompounds(newCompounds, caseData);
    // integrateCompounds(responseData, caseData);
    // CompoundHeritage.addTerminalReplacement(
    //     compound, newCompounds, { fragsetName, fromAtom }
    // );
    // const errors = responseData.byId(ResponseIds.Error);
    // App.Workspace.activateFirstOf(newCompounds);

    return { compounds: [], errors: [] };
}

/**
 * @param {import('BMapsModel').Atom} atom
 * @param {string} replacementGroup
 * @returns {Promise<{
 *  errors: string[]
 *  compounds: Compound[]
 * }>}
 */
async function replaceGroup(atom, replacementGroup) {
    const { atomGroup: compound, connector, caseData } = App.getDataParents(atom);
    await UserActions.SelectAtom(null);
    const responseData = await connector.cmdReplaceGroup(replacementGroup, atom.uniqueID);
    const newCompounds = responseData.byId(ResponseIds.Compound);
    App.Workspace.addReplaceCompounds(newCompounds, caseData);
    integrateCompounds(responseData, caseData);
    CompoundHeritage.addTerminalReplacement(compound, newCompounds, { replacementGroup, atom });
    const errors = responseData.byId(ResponseIds.Error);
    App.Workspace.activateFirstOf(newCompounds);

    return { compounds: newCompounds, errors };
}

/**
 * Search for fragments that can grow from a bond vector defined by two atoms.
 * @param {[Atom, Atom]} bondVectorPair - [fromAtom, terminalAtom] defining the bond vector
 * @param {string} findType - Type of search (e.g. 'Bond', 'Near')
 * @param {string} [scope='best'] - Search scope passed to the server
 * @param {number|null} [searchTolerance=null] - Tolerance for distance/angle matching
 * @returns {Promise<{ suggestions: BfdServerFragmentSearchResult[], errors: string[] }>}
 */
async function growFromAtom(bondVectorPair, findType, scope='best', searchTolerance=null) {
    if (bondVectorPair.length !== 2) {
        return { suggestions: [], errors: ['No bond vector specified.'] };
    }
    const [fromAtom, toAtom] = bondVectorPair;
    const { connector, caseData } = App.getDataParents(fromAtom);

    await UserActions.SelectAtom(null);
    const responseData = await connector.cmdFindModifications({
        findType,
        scope,
        searchParams: {
            baseAtomUid: fromAtom.uniqueID,
            terminalAtomUid: toAtom.uniqueID,
            searchTolerance,
        },
    });
    const [fragments=[]] = responseData.byId(ResponseIds.FindResults);
    const [suggestions=[]] = responseData.byId(ResponseIds.ModificationSelections);
    BfdServerFragmentSearchResult.syncResultFragments(suggestions, fragments, caseData);
    FragmentSearchResult.calculatePoseStatistics(suggestions);
    const errors = responseData.byId(ResponseIds.Error);
    return { suggestions, errors };
}

/**
 * Search for fragments located near a given atom.
 * @param {Atom} atom - The reference atom to search around
 * @param {string} [scope='best'] - Search scope passed to the server
 * @param {number|null} [searchRadius=null] - Radius in Angstroms within which to search
 * @param {number|null} [searchTolerance=null] - Tolerance for distance matching
 * @returns {Promise<{ suggestions: BfdServerFragmentSearchResult[], errors: string[] }>}
 */
async function searchNear(atom, scope='best', searchRadius=null, searchTolerance=null) {
    const { connector, caseData } = App.getDataParents(atom);
    await UserActions.SelectAtom(null);
    const responseData = await connector.cmdFindModifications({
        findType: 'Near',
        scope,
        searchParams: {
            baseAtomUid: atom.uniqueID,
            searchRadius,
            searchTolerance,
        },
    });
    const [fragments=[]] = responseData.byId(ResponseIds.FindResults);
    const [suggestions=[]] = responseData.byId(ResponseIds.ModificationSelections);
    BfdServerFragmentSearchResult.syncResultFragments(suggestions, fragments, caseData);
    FragmentSearchResult.calculatePoseStatistics(suggestions);
    const errors = responseData.byId(ResponseIds.Error);
    return { suggestions, errors };
}

/**
 * Record compound heritage for a fragment modification, for either search path (dataservice or
 * bfd-server). Find-near has no compound parent unless the source is itself a compound.
 * @param {string} modType - 'Near' for find-near, otherwise a fragment grow
 * @param {import('BMapsModel').AtomGroup} atomGroup - the source atom group
 * @param {import('BMapsModel').Compound[]} newCompounds - the resulting compounds
 * @param {{ suggestion: FragmentSearchResult, pose: DecodedFragment, atom: Atom }} detailObj
 */
function recordModificationHeritage(modType, atomGroup, newCompounds, detailObj) {
    if (modType === 'Near') {
        const original = atomGroup instanceof Compound ? atomGroup : null;
        CompoundHeritage.addFindNear(original, newCompounds, detailObj);
    } else {
        CompoundHeritage.addFragmentGrow(atomGroup, newCompounds, detailObj);
    }
}

/**
 * @param {FragmentSearchResult} suggestion
 * @param {Atom} sourceAtom - the reference atom for the fragment search
 * @param {DecodedFragment?} pose - the specific pose to use if not default
 * @returns {Promise<{ compounds: Compound[], errors: string[] }>}
 */
async function confirmModification(suggestion, sourceAtom, pose) {
    const { frags, modType, origin } = suggestion;
    const frag = pose ?? frags[0];
    const { atomGroup, connector, caseData } = App.getDataParents(sourceAtom);

    if (origin === 'dataservice') {
        // For dataservice suggestions, send assemble compound cmd
        let cmdPromise;
        if (modType === 'Near') {
            cmdPromise = UserActions.AssembleCompound({
                srcMol: null,
                modMol: frag,
                modInfo: {
                    type: modType,
                    modAttachAtom: frag.atoms[0].atom,
                },
            }, caseData);
        } else { // Fragment grow
            cmdPromise = UserActions.AssembleCompound({
                srcMol: atomGroup,
                modMol: frag,
                modInfo: {
                    type: modType,
                    srcAttachAtom: sourceAtom.atom,
                    srcDirectionAtom: suggestion.srcDirectionAtomName || '',
                    modAttachAtom: suggestion.fragAtomName || '',
                    modDirectionAtom: suggestion.fragDirectionAtomName || '',
                },
            }, caseData);
        }
        const result = await cmdPromise;
        recordModificationHeritage(modType, atomGroup, result.compounds, {
            suggestion, pose: frag, atom: sourceAtom,
        });
        return result;
    }

    // For suggestions from bfd-server (original find near / find bond logic)
    // Send select modification cmd with selectionID that the server recognizes for the pose
    const selectionID = frag.poseSerialNo;
    const responseData = await connector.cmdSelectModification(modType, selectionID);
    const newCompounds = responseData.byId(ResponseIds.Compound);
    App.Workspace.addReplaceCompounds(newCompounds, caseData);
    integrateCompounds(responseData, caseData);
    recordModificationHeritage(modType, atomGroup, newCompounds, {
        suggestion, pose: frag, atom: sourceAtom,
    });
    const errors = responseData.byId(ResponseIds.Error);
    App.Workspace.activateFirstOf(newCompounds);

    return { compounds: newCompounds, errors };
}

/**
 * Grow from a bond vector to a specifically named fragment, then confirm the modification.
 * Delegates to growFromAtom and confirmModification.
 * @param {[Atom, Atom]} bondVectorPair - [fromAtom, terminalAtom] defining the bond vector
 * @param {string} findType - Type of search (e.g. 'Bond')
 * @param {string} fragName - Name of the fragment to select from search results
 * @param {string} [scope='best'] - Search scope passed to the server
 * @returns {Promise<{ compounds: Compound[], errors: string[] }>}
 */
async function growToFragmentName(bondVectorPair, findType, fragName, scope='best') {
    const { suggestions, errors } = await growFromAtom(bondVectorPair, findType, scope);

    let suggestion;
    for (const sugg of suggestions) {
        if (sugg.name === fragName) {
            suggestion = sugg;
            break;
        }
    }

    if (!suggestion) {
        return { compounds: [], errors: [`Could not find fragment ${fragName} near selection`] };
    }

    const result = await confirmModification(suggestion, bondVectorPair[0]);
    return result;
}

/**
 * Submit a query to the frag-data-query service, converting a client-side request object
 * to the server format.
 * When the time comes to add more link types and reaction awareness,
 * the query structure may need to be refactored (findType, findNearAtom, findBondAtoms).
 * @param {{
 *  findNearAtom?: Atom,
 *  findBondAtoms?: [Atom, Atom],
 *  findType?: 'Near' | 'Bond',
 *  mapCases: import('BMapsModel').MapCase[],
 *  searchRadius?: number,
 *  maxBValue?: number,
 *  fragsets?: object[],
 *  fragmentNames?: string[],
 * }} userQuery
 * @param {object} [connector=App.ServerConnection]
 * @returns {Promise<{ suggestions: DataServiceFragmentSearchResult[], errors: string[] }>}
 */
async function fragDataQuery(userQuery, connector=App.ServerConnection) {
    const queryObj = makeFragDataQuery(userQuery);
    console.log(`FragDataQuery: ${JSON.stringify(queryObj)}`);
    const responseData = await connector.cmdFragDataQuery(queryObj);
    const results = responseData.byId(ResponseIds.FragDataResults);
    const errors = responseData.errors;

    let suggestions = [];
    let fragments = [];
    if (results.length > 0) {
        const [{ fragments: resultFragments, errors: resultErrors }] = results;
        fragments = resultFragments;
        if (resultErrors?.length) {
            errors.push(...resultErrors);
        } else if (userQuery.findNearAtom || userQuery.findBondAtoms) {
            suggestions = makeDataServiceSuggestions(fragments, userQuery);
        } else {
            const msg = 'Can not process this query: no findNearAtom or bondVectorPair';
            errors.push(msg);
            console.warn(msg);
        }
    }

    return { suggestions, errors };
}

/**
 * Create client-side fragment suggestion objects from dataservice decoded fragments.
 * Supports three search modes, selected by findType:
 *  - 'Near' + findNearAtom: dataservice radius search, return all fragments as-is.
 *  - 'Near' + findBondAtoms: dataservice cone search (Vector Search), return all fragments as-is.
 *  - 'Bond' + findBondAtoms: Fragment Growing — filter fragments per linker distance/angle
 *    geometry and remove clashes. findType defaults to 'Bond'.
 *  When the time comes to add more link types and reaction awareness,
 *  this query structure may need to be refactored.
 *
 * @param {DecodedFragment[]} fragments - Decoded fragment poses from the data service
 * @param {{
 *  mapCases: import('BMapsModel').MapCase[],
 *  findType: 'Near' | 'Bond',
 *  findBondAtoms?: [Atom, Atom],
 * }} userQuery - The original user query providing search context
 * @returns {DataServiceFragmentSearchResult[]}
 */
function makeDataServiceSuggestions(fragments, userQuery) {
    const suggMap = new Map();
    const {
        mapCases, findBondAtoms, findType: searchFindType='Bond',
    } = userQuery;

    // Set fragment caseData so coordinates can be transformed for bond search and downstream
    for (const frag of fragments) {
        const fragMapCase = mapCases.find((mapCase) => mapCase.projectCase === frag.projectCase);
        frag.setCaseData(App.Workspace.lookupCaseData(fragMapCase));
    }

    let fragmentEntries = [];
    if (searchFindType === 'Near') {
        // 'Near' covers both single-atom and vector search: the dataservice has already
        // filtered by proximity/direction, so use all returned fragments as-is.
        const attachmentInfo = { modType: searchFindType };
        fragmentEntries = fragments.map((frag) => ({ frag, attachmentInfo }));
    } else if (findBondAtoms) {
        // Fragment Growing. Apply client-side bond geometry and clash removal.
        const searchParams = {
            findType: searchFindType,
            bondVectorAtomPairs: [findBondAtoms],
        };
        let growResults = bondableSearchResults(searchParams, fragments);
        growResults = removeClashingFragmentResults(growResults);

        const foundFragsByType = {};
        for (const {
            startTerminal, endAtom, endTerminal, findType,
        } of growResults) {
            const frag = endAtom.fragment;
            const fragAtomName = endAtom.atom;
            const fragDirectionAtomName = endTerminal?.atom;
            const attachmentInfo = {
                modType: findType,
                srcDirectionAtomName: startTerminal?.atom,
                fragAtomName,
                fragDirectionAtomName,
            };
            const key = FragmentSearchResult.makeKey({ frag, attachmentInfo });
            if (!foundFragsByType[key]) {
                foundFragsByType[key] = { attachmentInfo, fragSet: new Set() };
            }

            foundFragsByType[key].fragSet.add(frag);
        }

        for (const { fragSet, attachmentInfo } of Object.values(foundFragsByType)) {
            for (const frag of fragSet) {
                fragmentEntries.push({ frag, attachmentInfo });
            }
        }
    }

    for (const { frag, attachmentInfo } of fragmentEntries) {
        // Waters aren't explicitly handled by the dataservice, but they can get included with
        // fragment data. Ignore them until they can be handled in a rational way,
        // otherwise browser could run out of memory.
        if (frag.baseFrag.name === 'water') continue;

        const key = FragmentSearchResult.makeKey({ frag, attachmentInfo });
        const sugg = suggMap.get(key);
        if (sugg) {
            sugg.addFrag(frag);
        } else {
            const newSugg = new DataServiceFragmentSearchResult(frag, mapCases, attachmentInfo);
            suggMap.set(key, newSugg);
        }
    }

    const suggestions = [...suggMap.values()];
    FragmentSearchResult.calculatePoseStatistics(suggestions);
    const totalPoses = suggestions.reduce(
        (poseCount, nextSugg) => poseCount + nextSugg.frags.length, 0
    );
    console.log(`Created ${suggMap.size} modification suggestions with ${totalPoses} total poses.`);

    return suggestions;
}

/**
 * Convert a client side frag-data query into a query object recognized by the data service.
 * @param {{
 *  findNearAtom: import('BMapsModel').Atom,
 *  findBondAtoms: [import('BMapsModel').Atom, import('BMapsModel').Atom],
 *  mapCases: import('BMapsModel').MapCase[]
 * }} arg1
 *
 * This could be moved somewhere else?
 */
function makeFragDataQuery({
    findNearAtom, findBondAtoms, mapCases=[],
    searchRadius=5, maxBValue=-5,
    fragsets, fragmentNames,
}={}) {
    if (findNearAtom && findBondAtoms) {
        const atomDesc = getFullResidueId(findNearAtom);
        const vectorDesc = joinAtomSpecs(findBondAtoms, ', ');
        console.warn(`Attempting frag-data-query for both findNear ${atomDesc} and findBond ${vectorDesc}.`);
    }

    /**
     * Build filter entries restricting results to the given map cases' project cases.
     * @param {import('BMapsModel').MapCase[]} cases
     * @returns {Array[]}
     */
    function filtersForMapCases(cases) {
        const projectCases = new Set();

        for (const mapCase of cases) {
            projectCases.add(mapCase.projectCase);
        }
        return [
            ['dataSource', '=', 'public'],
            ['projectCase', 'in', [...projectCases]],
        ];
    }

    /**
     * Build filter entries restricting results to the configured fragment names and B-value cutoff.
     * @returns {Array[]}
     */
    function fragmentFilters() {
        const filters = [];

        const fragnames = new Set();
        for (const fragset of fragsets) {
            for (const frag of fragset.items) {
                fragnames.add(frag.name);
            }
        }
        for (const fragName of fragmentNames) {
            fragnames.add(fragName);
        }
        if (fragnames.size > 0) {
            filters.push(['fragment', 'in', [...fragnames]]);
        }
        // filters.push(
        //     ['fragment', 'in', [
        //         'acetamide', 'acetamidine_E', 'acetic_acid', 'acetone',
        //         'aniline', 'benzene', 'dimethylamine', 'ethanol',
        //         'ether', 'ethylamine', 'furan', 'i-butane',
        //         'i-propylamine', 'imidazole', 'phenol', 'propane',
        //     ]]
        // );
        filters.push(['B', '<=', maxBValue]);
        return filters;
    }
    /**
     * Build a perPosture FindNear filter for the given atom, or an empty array if no atom.
     * @param {Atom|null} atom
     * @returns {Array[]}
     */
    function findNearFilters(atom) {
        if (!atom) return [];

        return [
            ['perPosture', 'FindNear', {
                center: atom.getPosition(),
                radius: searchRadius,
            }],
        ];
    }
    /**
     * Build a perPosture FindDirectional filter from a bond vector atom pair, or empty if no atoms.
     * @param {[Atom, Atom]|null} atoms
     * @returns {Array[]}
     */
    function findBondFilters(atoms) {
        if (!atoms) return [];
        const startPos = atoms[0].getPosition();
        const endPos = atoms[1].getPosition();
        return [
            ['perPosture', 'FindDirectional', {
                center: startPos,
                direction: Sub3_3(endPos, startPos),
                rmin: 0,
                rmax: searchRadius,
                dtheta: 1.25, // about 71.6 deg
            }],
        ];
    }

    /**
     * Convert a USAlign transform to the six-float format expected by the data service:
     * [tx, ty, tz, ox, oy, oz] (translation + orientation vectors).
     * The orientation vector is the unit vector for the axis of rotation,
     * multiplied by the rotation angle counterclockwise around that vector,
     * with +1.0 offset to avoid quantization errors near zero (angle range: 1.0-1.0+pi).
     * USAlign is "rotate then translate"; data service expects the transpose.
     *
     * See https://dev-server.boltzmannmaps.com/wiki/index.php/BMapsDS,_Query_Format
     * @param {{ translation: number[], rotation: number[][] }} usalignTransform
     * @returns {number[]} Six-element array [tx, ty, tz, ox, oy, oz]
     */
    function toDataServiceTransform(usalignTransform) {
        const { translation, rotation } = TransposeRTTransform(usalignTransform);
        const offsetForDataservice = 1; // see above
        const orientationVector = matrixToOrientation(rotation, offsetForDataservice);
        return [...translation, ...orientationVector];
    }

    /**
     * Build the case-extras object for a single map case, including a transform if the case
     * has a reference case (i.e. was aligned to another structure).
     * @param {import('BMapsModel').MapCase} mapCase
     * @returns {{ transform?: number[] }}
     */
    function oneCaseExtras(mapCase) {
        const extras = {};
        /** @type {CaseData} */
        const caseData = App.Workspace.lookupCaseData(mapCase);
        const refCase = caseData.getReferenceCaseData();
        if (refCase) {
            const transform = caseData.getWholeProteinTransform();
            extras.transform = toDataServiceTransform(transform);
        }
        return extras;
    }

    /**
     * Build a map of projectCase → case-extras for all provided map cases.
     * @param {import('BMapsModel').MapCase[]} cases
     * @returns {Object.<string, { transform?: number[] }>}
     */
    function caseExtras(cases) {
        const ret = {};
        for (const mapCase of cases) {
            ret[mapCase.projectCase] = oneCaseExtras(mapCase);
        }
        return ret;
    }

    const queryObj = {
        op: 'frag-query',
        'structure-version': 1,
        'filter-series': [
            ...filtersForMapCases(mapCases),
            ...fragmentFilters(),
            ...findNearFilters(findNearAtom),
            ...findBondFilters(findBondAtoms),
            // This case-extras is a workaround
            ['case-extras', '', caseExtras(mapCases)],
        ],
        // This case-extras is per the spec
        'case-extras': caseExtras(mapCases),
    };
    return queryObj;
}
